/**
 * @copyright
 * Copyright(c) 2019 SICK AG
 */

/**
 * This class holds constants and utility functions related to CSS styles
 */
export class CssTools {
  /**
   * Converts a value in rem to a value in pixels.
   *
   * @param rem - a value in rem
   * @returns the given value converted to pixels
   */
  static REM2PIXEL(rem) {
    // Do not cache this value. The font size could change during runtime
    // because another style-sheet is loaded or the style of the root element
    // is altered via a piece of script.
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return rem * rootFontSize;
  }
  /**
   * Computes the line-height for a font-size in rem.
   * This computation is an estimate and independent of the actual font-family or font-weight.
   * @param fontSizeInRem - the font-size in rem
   * @returns the height of a line of text with the given font-size
   */


  static REM2LINE_HEIGHT(fontSizeInRem) {
    return CssTools.PIXELS2LINE_HEIGHT(CssTools.REM2PIXEL(fontSizeInRem));
  }
  /**
   * Computes the line-height for a font-size in pixels.
   * This computation is an estimate and independent of the actual font-family or font-weight.
   * @param fontSizeInPixels - the font-size in pixels
   * @returns the height of a line of text with the given font-size
   */


  static PIXELS2LINE_HEIGHT(fontSizeInPixels) {
    // 1.45 is an empiric value. It is the largest scaling factor that has
    // been measured on Windows 10, google chrome browser, for several
    // combinations of font-families and font-sizes. No line-height should
    // exceed that value.
    return fontSizeInPixels * 1.45;
  }
  /**
   * Retrieves the computed font-family for the given element.
   *
   * @param element - the element to get the computed font-family for
   * @returns the computed font-family for the given element
   */


  static GET_COMPUTED_FONT_FAMILY(element) {
    return getComputedStyle(element).fontFamily;
  }
  /**
   * Retrieves the computed font-size in pixels for the given element.
   *
   * @param element - the element to get the computed font-size for
   * @returns the computed font-size in pixels for the given element
   */


  static GET_COMPUTED_FONT_SIZE(element) {
    return parseFloat(getComputedStyle(element).fontSize);
  }
  /**
   * Retrieves the computed font-weight for the given element.
   *
   * @param element - the element to get the computed font-weight for
   * @returns the computed font-weight for the given element
   */


  static GET_COMPUTED_FONT_WEIGHT(element) {
    const strFontWeight = getComputedStyle(element).fontWeight.toLowerCase(); // special handling for the Safari browser

    switch (strFontWeight) {
      case 'normal':
        return 400.0;

      case 'bold':
        return 700.0;

      default:
        // probably a number; values like 'lighter' or 'bolder' will not be considered
        break;
    }

    const fontWeight = parseFloat(strFontWeight);

    if (isNaN(fontWeight)) {
      // fall back to normal font weight
      return 400.0;
    }

    return fontWeight;
  }
  /**
   * Retrieves the computed font-style for the given element.
   *
   * @param element - the element to get the computed font-style for
   * @returns the computed font-style for the given element
   */


  static GET_COMPUTED_FONT_STYLE(element) {
    return getComputedStyle(element).fontStyle;
  }
  /**
   * Retrieves the computed background-color for the given element.
   *
   * @param element - the element to get the computed background-color for
   * @returns the computed background-color for the given element or null if the element is undefined or no background-color is set
   */


  static GET_COMPUTED_BG_COLOR(element) {
    return element ? CssTools._mapTransparentToNull(getComputedStyle(element).backgroundColor) : null;
  }
  /**
   * Retrieves the computed color for the given element.
   *
   * @param element - the element to get the computed color for
   * @returns the computed color for the given element or null if the element is undefined or no color is set
   */


  static GET_COMPUTED_COLOR(element) {
    return element ? CssTools._mapTransparentToNull(getComputedStyle(element).color) : null;
  }
  /**
   * Retrieves the computed border color for the left site of the given element.
   *
   * @param element - the element to get the computed border color for
   * @returns the computed border color for the left site of the given element or null if the element is undefined or no border color is set
   */


  static GET_COMPUTED_BORDER_LEFT_COLOR(element) {
    return element ? CssTools._mapTransparentToNull(getComputedStyle(element).borderLeftColor) : null;
  }

  static _mapTransparentToNull(color) {
    // This is a workaround to detect an unset color;
    // Chrome and FF return 'rgba(0, 0, 0, 0)' if the color is not set.
    // IE11 returns 'transparent' if the color is not set. See computeBgColor.html.
    if (color === 'rgba(0, 0, 0, 0)' || color === 'transparent' || color === '') {
      return null;
    }

    return color;
  }
  /**
   * Retrieves the fallback value from the given variable if present
   *
   * @param cssVariable - the variable to retrieve the fallback value from
   * @returns the fallback value of an empty string
   */


  static GET_FALLBACK_VALUE_FROM_VARIABLE(cssVariable) {
    const indexStart = cssVariable.lastIndexOf(',');
    const indexEnd = cssVariable.lastIndexOf(')');

    if (indexStart > -1 && indexEnd > -1) {
      return cssVariable.substring(indexStart + 1, indexEnd);
    }

    return '';
  }

}
